import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const FilterPanel = ({ onFilterChange, onReset }) => {
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    userId: '',
    actionType: '',
    dataCategory: '',
    sensitivityLevel: []
  });

  const actionTypes = [
    { value: 'all', label: 'Alle Aktionen' },
    { value: 'create', label: 'Erstellen' },
    { value: 'update', label: 'Aktualisieren' },
    { value: 'delete', label: 'Löschen' },
    { value: 'view', label: 'Ansehen' },
    { value: 'export', label: 'Exportieren' },
    { value: 'login', label: 'Anmeldung' },
    { value: 'logout', label: 'Abmeldung' }
  ];

  const dataCategories = [
    { value: 'all', label: 'Alle Kategorien' },
    { value: 'client_data', label: 'Kundendaten' },
    { value: 'user_management', label: 'Benutzerverwaltung' },
    { value: 'system_config', label: 'Systemkonfiguration' },
    { value: 'reports', label: 'Berichte' },
    { value: 'bulk_operations', label: 'Massenoperationen' }
  ];

  const handleFilterChange = (field, value) => {
    const updatedFilters = { ...filters, [field]: value };
    setFilters(updatedFilters);
    onFilterChange(updatedFilters);
  };

  const handleSensitivityToggle = (level) => {
    const updated = filters?.sensitivityLevel?.includes(level)
      ? filters?.sensitivityLevel?.filter(l => l !== level)
      : [...filters?.sensitivityLevel, level];
    handleFilterChange('sensitivityLevel', updated);
  };

  const handleResetFilters = () => {
    const resetFilters = {
      dateFrom: '',
      dateTo: '',
      userId: '',
      actionType: '',
      dataCategory: '',
      sensitivityLevel: []
    };
    setFilters(resetFilters);
    onReset();
  };

  return (
    <div className="bg-card border-r border-border h-full overflow-y-auto scrollbar-thin">
      <div className="p-4 border-b border-border sticky top-0 bg-card z-10">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Icon name="Filter" size={20} />
            Erweiterte Filter
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleResetFilters}
            iconName="RotateCcw"
            iconSize={16}
          >
            Zurücksetzen
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Verfeinern Sie Ihre Audit-Suche
        </p>
      </div>
      <div className="p-4 space-y-6">
        {/* Date Range */}
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
            <Icon name="Calendar" size={16} />
            Zeitraum
          </h3>
          <Input
            type="date"
            label="Von"
            value={filters?.dateFrom}
            onChange={(e) => handleFilterChange('dateFrom', e?.target?.value)}
            max={new Date()?.toISOString()?.split('T')?.[0]}
          />
          <Input
            type="date"
            label="Bis"
            value={filters?.dateTo}
            onChange={(e) => handleFilterChange('dateTo', e?.target?.value)}
            max={new Date()?.toISOString()?.split('T')?.[0]}
          />
        </div>

        {/* User Selection */}
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
            <Icon name="User" size={16} />
            Benutzer
          </h3>
          <Input
            type="text"
            placeholder="Benutzer-ID oder Name"
            value={filters?.userId}
            onChange={(e) => handleFilterChange('userId', e?.target?.value)}
          />
        </div>

        {/* Action Type */}
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
            <Icon name="Activity" size={16} />
            Aktionstyp
          </h3>
          <Select
            options={actionTypes}
            value={filters?.actionType}
            onChange={(value) => handleFilterChange('actionType', value)}
            placeholder="Aktion auswählen"
          />
        </div>

        {/* Data Category */}
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
            <Icon name="Database" size={16} />
            Datenkategorie
          </h3>
          <Select
            options={dataCategories}
            value={filters?.dataCategory}
            onChange={(value) => handleFilterChange('dataCategory', value)}
            placeholder="Kategorie auswählen"
          />
        </div>

        {/* Sensitivity Level */}
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
            <Icon name="Shield" size={16} />
            Sensibilitätsstufe
          </h3>
          <div className="space-y-2">
            <Checkbox
              label="Öffentlich"
              checked={filters?.sensitivityLevel?.includes('public')}
              onChange={() => handleSensitivityToggle('public')}
            />
            <Checkbox
              label="Intern"
              checked={filters?.sensitivityLevel?.includes('internal')}
              onChange={() => handleSensitivityToggle('internal')}
            />
            <Checkbox
              label="Vertraulich"
              checked={filters?.sensitivityLevel?.includes('confidential')}
              onChange={() => handleSensitivityToggle('confidential')}
            />
            <Checkbox
              label="Streng vertraulich"
              checked={filters?.sensitivityLevel?.includes('restricted')}
              onChange={() => handleSensitivityToggle('restricted')}
            />
          </div>
        </div>

        {/* Quick Filters */}
        <div className="space-y-3 pt-4 border-t border-border">
          <h3 className="text-sm font-medium text-foreground flex items-center gap-2">
            <Icon name="Zap" size={16} />
            Schnellfilter
          </h3>
          <div className="space-y-2">
            <Button
              variant="outline"
              size="sm"
              fullWidth
              onClick={() => {
                const today = new Date()?.toISOString()?.split('T')?.[0];
                handleFilterChange('dateFrom', today);
                handleFilterChange('dateTo', today);
              }}
            >
              Heute
            </Button>
            <Button
              variant="outline"
              size="sm"
              fullWidth
              onClick={() => {
                const lastWeek = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];
                const today = new Date()?.toISOString()?.split('T')?.[0];
                handleFilterChange('dateFrom', lastWeek);
                handleFilterChange('dateTo', today);
              }}
            >
              Letzte 7 Tage
            </Button>
            <Button
              variant="outline"
              size="sm"
              fullWidth
              onClick={() => {
                const lastMonth = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];
                const today = new Date()?.toISOString()?.split('T')?.[0];
                handleFilterChange('dateFrom', lastMonth);
                handleFilterChange('dateTo', today);
              }}
            >
              Letzte 30 Tage
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterPanel;